import { library } from '@fortawesome/fontawesome-svg-core';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { S_Dropzone } from '../../../../design/dropzone/S_Dropzone';
import { S_ErrorFeedback } from '../../../../design/feedback/errorFeedback/S_ErrorFeedback';
import { S_ClipLoader } from '../../../../design/loaders/clipLoader/S_ClipLoader';
import { S_Modal } from '../../../../design/modals/modal/S_Modal';
import { useAttachmentsPermissions } from '../../../../modules/attachments/Attachments.permissions';
import { FileList } from '../../../components/FileList';
import { DisabledWhenPolicyExceeded } from '../../../containers/DisabledWhenPolicyExceeded';
import { EnableFor } from '../../../containers/EnableForRole';
import { IPreparedFile, IWillAttachmentUploadExceedsLimitDto, UploadStatus } from '../attachmentUploadStore';

library.add(faUpload);

interface IProps {
  isModalOpen: boolean;
  isProcessingImages: boolean;
  preparedFiles: IPreparedFile[];
  willExceedLimit: IWillAttachmentUploadExceedsLimitDto;
  toggleModal: () => void;
  handleFileDropped: (files: File[]) => Promise<void>;
  onFileUpload: () => void;
}

export const AttachmentUpload = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const permissions = useAttachmentsPermissions();
  const acceptedFiles = props.preparedFiles.filter((x) => x.status === UploadStatus.Valid);
  const rejectedFiles = props.preparedFiles.filter((x) => x.status !== UploadStatus.Valid);

  return (
    <>
      <DisabledWhenPolicyExceeded limitType='space'>
        <EnableFor hasPermission={permissions.canAddAttachments()}>
          <Button
            variant='outlined'
            onClick={props.toggleModal}
            startDecorator={<FontAwesomeIcon icon={faUpload} />}
          >
            {t('upload')}
          </Button>
        </EnableFor>
      </DisabledWhenPolicyExceeded>
      <S_Modal
        isOpen={props.isModalOpen}
        toggle={props.toggleModal}
        headerText={t('upload_attachments')}
        buttons={
          <>
            <Button
              color='neutral'
              variant='outlined'
              onClick={props.toggleModal}
            >
              {t('cancel')}
            </Button>
            <Button
              disabled={acceptedFiles.length === 0 || props.willExceedLimit.willExceedsAvailableSpace}
              onClick={props.onFileUpload}
            >
              {t('upload')}
            </Button>
          </>
        }
      >
        <strong>{t('please_select_your_files')}</strong>
        <S_Dropzone
          multiple={true}
          onDrop={(files) => {
            void props.handleFileDropped(files);
          }}
        >
          <FontAwesomeIcon icon={faUpload} />
          {t('drop_your_json_file_here_or_select_it_manually')}
        </S_Dropzone>
        {props.isProcessingImages && (
          <S_ClipLoader
            caption={t('processing_your_files')}
            color='#1F2046'
          />
        )}
        {acceptedFiles.length > 0 && props.willExceedLimit.willExceedsAvailableSpace && (
          <S_ErrorFeedback>
            {t('uploading_selected_file_will_exceed_workspace_owners_total_files_size_limit', {
              plan: t(props.willExceedLimit.ownerPlan),
            })}
          </S_ErrorFeedback>
        )}
        {(rejectedFiles.length > 0 || acceptedFiles.length > 0) && !props.isProcessingImages && (
          <div className='file-list-container'>
            <div className='valid'>
              <FontAwesomeIcon icon='check' /> <strong>{acceptedFiles.length}</strong> {t('files_ready_to_upload')}
            </div>
            <div className='invalid'>
              <FontAwesomeIcon icon='times' /> <strong>{rejectedFiles.length} </strong> {t('files_invalid')}
              {rejectedFiles.length > 0 && <span>:</span>}
            </div>
            <FileList
              items={rejectedFiles.map((f) => ({
                name: f.file.name,
                description: t(f.status),
              }))}
            />
          </div>
        )}
      </S_Modal>
    </>
  );
});
